import { useCallback, useRef, useState } from 'react'
import { useQueryClient } from 'react-query'
import axios from 'src/utils/axios'
import { API_BASE_URL } from 'src/constants'
import { IMPORT_FAILED_TEXT, importRefusalText } from 'src/utils/importRefusal'

// The import of an uploaded result file into one somatic analysis
// (POST somatic-imports/<analysis>, libra #1930).
//
// A refusal answers 400 with { error, code, import }. The server's sentence is
// shown as sent, except for the refusals about which files can be read, which
// read the one neutral sentence (owner rule: no format or vendor lists on
// screen). A file naming another sample (sample_mismatch) can be imported
// after the user confirms it is this patient's: the same request again with
// confirm_other_sample true. A server without these codes refuses with its
// error alone, read the same way, and ignores the flag.

// Words for the new refusals when an answer carries the code without its sentence.
export const IMPORT_REFUSAL_FALLBACK_TEXT = {
  already_imported:
    'The results in this upload are already in the analysis from an earlier upload, so nothing from it was added again.',
  partly_imported:
    'Some results in this upload are already in the analysis from an earlier upload, so nothing from it was imported.',
  sample_mismatch: "This file names a different sample from the analysis's, so nothing from it was imported.",
}

export const SAMPLE_MISMATCH = 'sample_mismatch'

// What a refusal answer reads as: { code, text }.
export const importRefusal = (body) => {
  const code = typeof body?.code === 'string' ? body.code : null
  const served = typeof body?.error === 'string' ? body.error.trim() : ''
  const text = importRefusalText(code, served) || served || IMPORT_REFUSAL_FALLBACK_TEXT[code] || IMPORT_FAILED_TEXT
  return { code, text }
}

/**
 * Imports one uploaded file into a somatic analysis. Never throws for a refusal.
 * @returns {Promise<{ state: 'imported', import: object }
 *   | { state: 'confirm_other_sample' | 'refused', code: string|null, text: string, import: object|null }>}
 *   'confirm_other_sample': the file names another sample; ask the user, then
 *   call again with { confirmOtherSample: true }.
 */
export const importIntoAnalysis = async (awId, vendorFileId, { confirmOtherSample = false } = {}) => {
  const body = { vendor_file_id: vendorFileId }
  if (confirmOtherSample) body.confirm_other_sample = true
  try {
    const { data } = await axios.post(`${API_BASE_URL}/somatic-imports/${awId}`, body)
    return { state: 'imported', import: data || null }
  } catch (error) {
    const response = error?.response
    if (response?.status !== 400 || !response.data || typeof response.data !== 'object') throw error
    const { code, text } = importRefusal(response.data)
    const ask = code === SAMPLE_MISMATCH && !confirmOtherSample
    return { state: ask ? 'confirm_other_sample' : 'refused', code, text, import: response.data.import || null }
  }
}

/**
 * The import with its confirm step. start(fileId) posts; when the server asks
 * about another sample, outcome.state is 'confirm_other_sample' and confirm()
 * re-sends with the flag; cancel() drops the outcome. An import that went in
 * refreshes the analysis's structural tables.
 */
export const useSomaticImport = (awId) => {
  const queryClient = useQueryClient()
  const [outcome, setOutcome] = useState(null)
  const [busy, setBusy] = useState(false)
  const fileRef = useRef(null)

  const send = useCallback(
    async (fileId, opts) => {
      fileRef.current = fileId
      setBusy(true)
      try {
        const result = await importIntoAnalysis(awId, fileId, opts)
        if (result.state === 'imported') queryClient.invalidateQueries(['structural', awId])
        setOutcome(result)
        return result
      } catch (error) {
        const result = { state: 'error', code: null, text: IMPORT_FAILED_TEXT, import: null }
        setOutcome(result)
        return result
      } finally {
        setBusy(false)
      }
    },
    [awId, queryClient],
  )

  const start = useCallback((fileId) => send(fileId, {}), [send])
  const confirm = useCallback(() => send(fileRef.current, { confirmOtherSample: true }), [send])
  const cancel = useCallback(() => setOutcome(null), [])

  return { outcome, busy, start, confirm, cancel }
}
